import { useState, useEffect } from 'react'
import { RefreshCw, ChevronDown, ChevronUp, ExternalLink, Clock, CheckCircle, XCircle } from 'lucide-react'
import { authService } from '../../services/auth'
import { fetchAuthSession } from 'aws-amplify/auth'

interface OrgStats {
  existing: number
  new: number
  removed: number
  finalCount: number
  durationSeconds: number
  newRepos?: string[]
  removedRepos?: string[]
  error?: string
}

interface IndexRun {
  runId: string
  status: string
  startTime: string
  startTimeIST: string
  endTime?: string
  endTimeIST?: string
  durationSeconds?: number
  environment: string
  orgs?: Record<string, OrgStats>
  totals?: {
    existingRepos: number
    newRepos: number
    removedRepos: number
    finalRepos: number
  }
  queryLambdaUpdated?: boolean
  newLambdaVersion?: string
  error?: string
}

const ADMIN_API_URL = (import.meta.env.VITE_ADMIN_API_URL || '').replace(/\/$/, '')

export function FaissIndexRuns() {
  const [runs, setRuns] = useState<IndexRun[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedRun, setExpandedRun] = useState<string | null>(null)
  const [selectedRun, setSelectedRun] = useState<IndexRun | null>(null)

  useEffect(() => {
    loadRuns()
  }, [])

  const getToken = async () => {
    let token = authService.getToken()
    if (!token) {
      const session = await fetchAuthSession()
      token = session.tokens?.idToken?.toString() || null
      if (token) authService.setToken(token)
    }
    return token
  }

  const loadRuns = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const res = await fetch(`${ADMIN_API_URL}/admin/faiss-index-runs`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error(`Failed to load: ${res.status}`)
      const data = await res.json()
      setRuns(data.runs || [])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const loadRunDetail = async (runId: string) => {
    try {
      const token = await getToken()
      if (!token) return

      const res = await fetch(`${ADMIN_API_URL}/admin/faiss-index-runs/${encodeURIComponent(runId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setSelectedRun(data.run)
      }
    } catch (err) {
      console.error('Failed to load run detail:', err)
    }
  }

  const toggleExpand = (runId: string) => {
    if (expandedRun === runId) {
      setExpandedRun(null)
      setSelectedRun(null)
    } else {
      setExpandedRun(runId)
      loadRunDetail(runId)
    }
  }

  const StatusBadge = ({ status }: { status: string }) => {
    const colors = status === 'SUCCESS' 
      ? 'bg-green-100 text-green-800' 
      : status === 'FAILED' 
        ? 'bg-red-100 text-red-800' 
        : 'bg-yellow-100 text-yellow-800'
    const Icon = status === 'SUCCESS' ? CheckCircle : status === 'FAILED' ? XCircle : Clock
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    )
  }

  if (loading) {
    return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-blue-600" /></div>
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button onClick={loadRuns} className="mt-2 text-red-600 hover:text-red-800 text-sm">Retry</button>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">FAISS Index Update History</h2>
        <button onClick={loadRuns} className="flex items-center text-blue-600 hover:text-blue-800 text-sm">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </button>
      </div>

      {runs.length === 0 ? (
        <p className="text-gray-500">No index runs found</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time (IST)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">New</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Removed</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lambda</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {runs.map((run) => (
                <>
                  <tr key={run.runId} className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand(run.runId)}>
                    <td className="px-4 py-3 text-sm">{run.startTimeIST}</td>
                    <td className="px-4 py-3"><StatusBadge status={run.status} /></td>
                    <td className="px-4 py-3 text-sm">{run.durationSeconds ? `${Math.round(run.durationSeconds / 60)}m ${run.durationSeconds % 60}s` : '-'}</td>
                    <td className="px-4 py-3 text-sm text-green-600 font-medium">+{run.totals?.newRepos || 0}</td>
                    <td className="px-4 py-3 text-sm text-red-600 font-medium">-{run.totals?.removedRepos || 0}</td>
                    <td className="px-4 py-3 text-sm text-orange-600 font-medium">{(run as any).totalFailed || 0}</td>
                    <td className="px-4 py-3 text-sm">{run.queryLambdaUpdated ? `v${run.newLambdaVersion}` : '-'}</td>
                    <td className="px-4 py-3">{expandedRun === run.runId ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</td>
                  </tr>
                  {expandedRun === run.runId && selectedRun && (
                    <tr key={`${run.runId}-detail`}>
                      <td colSpan={8} className="px-4 py-4 bg-gray-50">
                        <div className="space-y-4">
                          <div className="grid grid-cols-5 gap-4 text-sm">
                            {selectedRun.orgs && Object.entries(selectedRun.orgs).map(([org, stats]) => (
                              <div key={org} className="bg-white p-3 rounded border">
                                <div className="font-medium text-gray-900 mb-2">{org}</div>
                                <div className="text-xs space-y-1">
                                  <div>Existing: {stats.existing}</div>
                                  <div className="text-green-600">New: +{stats.new}</div>
                                  <div className="text-red-600">Removed: -{stats.removed}</div>
                                  <div>Final: {stats.finalCount}</div>
                                  <div className="text-gray-500">{stats.durationSeconds}s</div>
                                </div>
                                {stats.newRepos && stats.newRepos.length > 0 && (
                                  <div className="mt-2 pt-2 border-t">
                                    <div className="text-xs font-medium text-green-700 mb-1">New repos:</div>
                                    <div className="max-h-20 overflow-y-auto">
                                      {stats.newRepos.slice(0, 5).map((repo) => (
                                        <a key={repo} href={`https://github.com/${org}/${repo}`} target="_blank" rel="noopener noreferrer"
                                          className="text-xs text-blue-600 hover:underline flex items-center">
                                          {repo.length > 25 ? repo.slice(0, 25) + '...' : repo}
                                          <ExternalLink className="w-3 h-3 ml-1" />
                                        </a>
                                      ))}
                                      {stats.newRepos.length > 5 && <div className="text-xs text-gray-500">+{stats.newRepos.length - 5} more</div>}
                                    </div>
                                  </div>
                                )}
                                {(stats as any).failedRepos && (stats as any).failedRepos.length > 0 && (
                                  <div className="mt-2 pt-2 border-t">
                                    <div className="text-xs font-medium text-orange-700 mb-1">⚠️ Failed ({(stats as any).failedRepos.length}):</div>
                                    <div className="max-h-24 overflow-y-auto">
                                      {(stats as any).failedRepos.slice(0, 5).map((f: any) => (
                                        <div key={f.name || f.repo} className="text-xs mb-1">
                                          <a href={f.url || `https://github.com/${f.repo}`} target="_blank" rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline">{(f.name || f.repo || '').split('/').pop()}</a>
                                          <span className="text-red-500 ml-1">— {(f.error || '').slice(0, 60)}</span>
                                        </div>
                                      ))}
                                      {(stats as any).failedRepos.length > 5 && <div className="text-xs text-gray-500">+{(stats as any).failedRepos.length - 5} more</div>}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          {selectedRun.error && (
                            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
                              Error: {selectedRun.error}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
